import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import { FASTAPI_HOST } from '../settings/config';
import StoryTypeFilter, { getStoryTypeLabel } from '../components/StoryTypeFilter';

function formatDate(dtStr) {
  if (!dtStr) return '';
  const d = new Date(dtStr);
  return isNaN(d) ? dtStr : d.toLocaleString();
}

export default function LibraryPage({ t, showTitle = true, headerContent = null }) {
  const PAGE_SIZE = 5;

  const [stories, setStories] = useState([]);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [storyType, setStoryType] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async (pageToLoad) => {
    const p = pageToLoad ?? 1;

    setLoading(true);
    setError('');

    const offset = (p - 1) * PAGE_SIZE;

    try {
      // Fetch 1 extra item to determine whether "Next" page exists
      const res = await axios.get(`${FASTAPI_HOST}/api/library/stories`, {
        params: {
          limit: PAGE_SIZE + 1,
          offset,
          ...(storyType ? { story_type: storyType } : {}),
        },
      });

      let fetched = res.data?.stories || [];
      const nextPageExists = fetched.length > PAGE_SIZE;
      setHasNext(nextPageExists);

      if (nextPageExists) fetched = fetched.slice(0, PAGE_SIZE);

      // If user navigated beyond available pages (e.g., data changed), go back one page.
      if (p > 1 && fetched.length === 0) {
        setPage(p - 1);
        return;
      }

      setStories(fetched);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load library');
    } finally {
      setLoading(false);
    }
  }, [storyType]);

  useEffect(() => {
    load(page);
  }, [page, load]);

  return (
    <Box sx={{ width: '100%', maxWidth: 900, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        {headerContent || (showTitle && (
          <Typography
            variant="subtitle1"
            sx={{
              color: 'text.primary',
              fontWeight: 600,
              fontSize: { xs: 22, sm: 26 },
              lineHeight: 1.4,
            }}
          >
            {t?.sb_library || 'Library'}
          </Typography>
        ))}
        <StoryTypeFilter
          value={storyType}
          onChange={(nextType) => {
            setStoryType(nextType);
            setPage(1);
          }}
          t={t}
          disabled={loading}
          sx={{
            minWidth: 0,
            mb: 0,
            ml: headerContent ? 0 : 'auto',
            flex: { xs: 1, sm: '0 0 230px' },
          }}
        />
        <IconButton
          aria-label={t?.sb_refresh || 'Refresh'}
          onClick={() => load(page)}
          disabled={loading}
          size="small"
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Box>

      {error && <Typography sx={{ color: 'crimson', mb: 2 }}>{error}</Typography>}
      {loading && <Typography sx={{ mb: 2 }}>{t?.sb_loading || 'Loading...'} </Typography>}

      {!loading && stories.length === 0 && (
        <Typography sx={{ color: '#666' }}>{t?.sb_library_empty || 'No shared stories yet.'}</Typography>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {stories.map((s) => (
          <Box
            key={s.share_slug}
            sx={{
              border: '1px solid #eee',
              borderRadius: 2,
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0, flex: 1 }}>
              {s.cover_img ? (
                <Box
                  component="img"
                  src={s.cover_img}
                  alt={s.title ? `${s.title} cover` : 'Story cover'}
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: 1.5,
                    objectFit: 'cover',
                    border: '1px solid #f0f0f0',
                    flexShrink: 0,
                  }}
                  loading="lazy"
                />
              ) : (
                <Box
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: 1.5,
                    background: '#f6f6f6',
                    border: '1px solid #f0f0f0',
                    flexShrink: 0,
                  }}
                />
              )}

              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {s.title}
                </Typography>
                <Typography sx={{ color: '#555', fontSize: 13, fontWeight: 600 }}>
                  {getStoryTypeLabel(s.type || 1, t)}
                </Typography>
                <Typography sx={{ color: '#777', fontSize: 14 }}>
                  {t?.sb_shared_at || 'Shared'}: {formatDate(s.shared_at || s.created_at)}
                </Typography>
              </Box>
            </Box>

            <IconButton
              aria-label={t?.sb_open || 'Open'}
              onClick={() => window.open(`/public/story/${s.share_slug}`, '_blank')}
              disabled={!s.share_slug}
              size="small"
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.5, mt: 2 }}>
        <IconButton
          aria-label={t?.sb_prev || 'Prev'}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={loading || page === 1}
          size="small"
        >
          <ChevronLeftIcon />
        </IconButton>

        <Typography sx={{ color: '#666', px: 1, userSelect: 'none' }}>
          {page}
        </Typography>

        <IconButton
          aria-label={t?.sb_next || 'Next'}
          onClick={() => setPage((p) => p + 1)}
          disabled={loading || !hasNext}
          size="small"
        >
          <ChevronRightIcon />
        </IconButton>
      </Box>
    </Box>
  );
}
